import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { getTokens } from "../../styles/tokens";

/* Primitivos locais da página API (card, título de seção, badge de método). */

export const MONO =
  "'JetBrains Mono', 'Fira Code', 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    card: {
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
      padding: 20,
      minWidth: 0,
      [theme.breakpoints.up("md")]: { padding: 24 },
    },
    sectionHead: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      marginBottom: 16,
      minWidth: 0,
      [theme.breakpoints.down("xs")]: { flexWrap: "wrap" },
    },
    stepNo: {
      flex: "none",
      width: 28,
      height: 28,
      borderRadius: t.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: t.font,
      fontSize: 13,
      fontWeight: 600,
      backgroundColor: t.tint.soft,
      color: t.brand,
      fontVariantNumeric: "tabular-nums",
    },
    sectionTitle: {
      fontFamily: t.font,
      fontSize: 15,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.text,
      margin: 0,
    },
    sectionDesc: {
      fontFamily: t.font,
      fontSize: 13,
      lineHeight: 1.5,
      color: t.textSecondary,
      margin: "2px 0 0",
    },
    headRight: {
      marginLeft: "auto",
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 8,
      // no celular as ações descem para a linha de baixo, em vez de espremer o título
      [theme.breakpoints.down("xs")]: { marginLeft: 0, flexBasis: "100%", flexWrap: "wrap" },
    },
    method: {
      display: "inline-flex",
      alignItems: "center",
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.04em",
      padding: "2px 8px",
      borderRadius: t.radius.badge,
      backgroundColor: t.tint.soft,
      color: t.brand,
      lineHeight: 1.6,
      flex: "none",
    },
    path: {
      fontFamily: MONO,
      fontSize: 13,
      color: t.text,
      wordBreak: "break-all",
    },
    label: {
      fontFamily: t.font,
      fontSize: 12.5,
      fontWeight: 600,
      color: t.textSecondary,
      marginBottom: 6,
      display: "block",
    },
    hint: {
      fontFamily: t.font,
      fontSize: 12,
      color: t.textMuted,
      marginTop: 4,
    },
    groupTitle: {
      fontFamily: t.font,
      fontSize: 12.5,
      fontWeight: 600,
      color: t.textSecondary,
      margin: "0 0 8px",
    },
    inlineCode: {
      fontFamily: MONO,
      fontSize: "0.92em",
      padding: "1px 6px",
      borderRadius: t.radius.badge,
      backgroundColor: t.status.neutral.bg,
      color: t.text,
      wordBreak: "break-all",
    },
  };
});

export const useUi = useStyles;

export const Card = ({ className, style, children, ...rest }) => {
  const c = useStyles();
  return (
    <section className={[c.card, className].filter(Boolean).join(" ")} style={style} {...rest}>
      {children}
    </section>
  );
};

export const SectionHead = ({ step, title, description, right }) => {
  const c = useStyles();
  return (
    <div className={c.sectionHead}>
      {step != null && <span className={c.stepNo}>{step}</span>}
      <div style={{ minWidth: 0 }}>
        <h2 className={c.sectionTitle}>{title}</h2>
        {description && <p className={c.sectionDesc}>{description}</p>}
      </div>
      {right && <div className={c.headRight}>{right}</div>}
    </div>
  );
};

export const MethodBadge = ({ method = "POST" }) => {
  const c = useStyles();
  return <span className={c.method}>{method}</span>;
};

export const Code = ({ children }) => {
  const c = useStyles();
  return <code className={c.inlineCode}>{children}</code>;
};

export const FieldLabel = ({ children, htmlFor }) => {
  const c = useStyles();
  return (
    <label className={c.label} htmlFor={htmlFor}>
      {children}
    </label>
  );
};

export default useStyles;
